import React, { useState, useEffect, useContext, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logoSrc from '../assets/Header.png';
import ReactGA from 'react-ga4';
import { AuthContext } from '../context/AuthContext';

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [moreOpen, setMoreOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const moreRef = useRef(null);
  const userMenuRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const setVhVar = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };
    setVhVar();
    window.addEventListener('resize', setVhVar);
    window.addEventListener('orientationchange', setVhVar);
    return () => {
      window.removeEventListener('resize', setVhVar);
      window.removeEventListener('orientationchange', setVhVar);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target))
        setMoreOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target))
        setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const mainLinks = [
    { label: 'Home', to: '/' },
    { label: 'Partner Banks', to: '/partner-banks' },
    { label: 'Credit Cards', to: '/credit-cards' },
    { label: 'Offers & News', to: '/offers-news' },
    { label: 'Book Consultation', to: '/consultation' },
  ];

  const moreLinks = [
    { label: 'Smart Assistant', to: '/smart-assistant' },
    { label: 'Calculators', to: '/calculators' },
  ];

  const allMobileLinks = [...mainLinks, ...moreLinks];

  const handleNavLinkClick = (label) => {
    ReactGA.event({ category: 'Navbar', action: 'Click', label });
  };

  const isCreditCardPage = location.pathname.startsWith('/credit-cards');
  const isOffersPage =
    location.pathname.startsWith('/offers-news') ||
    location.pathname.includes('/OfferAndNewsDetail') ||
    location.pathname.includes('/news-detail');

  const isActive = (to) => {
    if (to === '/partner-banks')
      return location.pathname.startsWith('/partner-banks');
    if (to === '/credit-cards') return isCreditCardPage;
    if (to === '/offers-news') return isOffersPage;
    return location.pathname === to;
  };

  const handleLoginSignup = () => {
    if (user) return;
    navigate('/login');
  };

  const fullName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : 'Account';

  return (
    <>
      <nav
        style={{ ...styles.navbar, ...(isMobile ? styles.mobileNavbar : {}) }}
      >
        {/* Left: Logo + Nav Links */}
        <div
          style={{
            ...styles.leftWrap,
            ...(isMobile ? styles.mobileLeftWrap : {}),
          }}
        >
          <div
            style={styles.logoWrap}
            onClick={() => navigate('/')}
            role="button"
          >
            <img src={logoSrc} alt="CardAcademy" style={styles.logoImg} />
          </div>

          {!isMobile && (
            <ul style={styles.linkListWrap}>
              <ul style={styles.linkList}>
                {mainLinks.map(({ label, to }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      style={{
                        ...styles.linkItem,
                        ...(isActive(to) ? styles.activeLink : {}),
                      }}
                      onMouseEnter={(e) => (e.target.style.color = '#21419C')}
                      onMouseLeave={(e) => {
                        e.target.style.color = isActive(to)
                          ? '#000000'
                          : '#9999A1';
                      }}
                      onClick={() => handleNavLinkClick(label)}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}

                {/* More dropdown */}
                <li style={{ position: 'relative' }} ref={moreRef}>
                  <button
                    style={{
                      ...styles.linkItem,
                      background: 'none',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    onClick={() => setMoreOpen((o) => !o)}
                  >
                    More
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#9999A1"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {moreOpen && (
                    <div style={styles.dropdown}>
                      {moreLinks.map(({ label, to }) => (
                        <NavLink
                          key={to}
                          to={to}
                          style={{
                            ...styles.dropdownItem,
                            ...(isActive(to) ? styles.dropdownItemActive : {}),
                          }}
                          onClick={() => {
                            setMoreOpen(false);
                            handleNavLinkClick(label);
                          }}
                        >
                          {label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </li>
              </ul>
            </ul>
          )}
        </div>

        {/* Right: User dropdown or Login/Signup */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {user ? (
              <div style={{ position: 'relative' }} ref={userMenuRef}>
                <button
                  style={styles.userDropdownBtn}
                  onClick={() => setUserMenuOpen((o) => !o)}
                >
                  <span style={styles.userDropdownName}>{fullName}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#1D4094"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {userMenuOpen && (
                  <div style={styles.userDropdown}>
                    <button
                      style={styles.userDropdownItem}
                      onClick={() => {
                        logout();
                        navigate('/');
                        setUserMenuOpen(false);
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                style={styles.loginSignupButton}
                onClick={handleLoginSignup}
              >
                Login/Signup
              </button>
            )}
          </div>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <div
            style={styles.hamburger}
            onClick={() => {
              window.scrollTo(0, 0);
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            aria-label="Open menu"
            role="button"
          >
            {isMobileMenuOpen ? (
              <span style={styles.closeGlyph}>✕</span>
            ) : (
              <>
                <div style={styles.bar} />
                <div style={styles.bar} />
                <div style={styles.bar} />
              </>
            )}
          </div>
        )}
      </nav>

      {/* Mobile fullscreen menu */}
      {isMobileMenuOpen && isMobile && (
        <div style={styles.modalOverlay} role="dialog" aria-modal="true">
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div
                style={styles.modalLogoWrap}
                onClick={() => {
                  navigate('/');
                  setIsMobileMenuOpen(false);
                }}
                role="button"
              >
                <img
                  src={logoSrc}
                  alt="CardAcademy"
                  style={styles.modalLogoImg}
                />
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                style={styles.modalClose}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <ul style={styles.mobileList}>
                {allMobileLinks.map(({ label, to }) => (
                  <li key={to} style={{ width: '100%' }}>
                    <NavLink
                      to={to}
                      style={{
                        ...styles.mobileLink,
                        ...(isActive(to) ? styles.activeMobileLink : {}),
                        display: 'block',
                        padding: '16px 0',
                        width: '100%',
                      }}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleNavLinkClick(label);
                      }}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: 20, textAlign: 'center' }}>
                {user ? (
                  <>
                    <div style={styles.mobileUserText}>{fullName}</div>
                    <button
                      style={styles.mobileLogoutButton}
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                        navigate('/');
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    style={styles.mobileLoginButton}
                    onClick={() => {
                      navigate('/login');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Login/Signup
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  navbar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px clamp(20px, 5vw, 80px)',
    backgroundColor: '#fff',
    boxShadow: '0 1px 5px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxSizing: 'border-box',
  },
  mobileNavbar: { padding: '15px 20px' },
  leftWrap: { display: 'flex', alignItems: 'center', flex: 1 },
  mobileLeftWrap: { justifyContent: 'space-between', width: '100%' },
  logoWrap: { width: '60px', height: '60px', cursor: 'pointer', flexShrink: 0 },
  logoImg: { width: '100%', height: '100%', objectFit: 'contain' },
  linkListWrap: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  linkList: {
    display: 'flex',
    listStyle: 'none',
    gap: 'clamp(12px, 2vw, 32px)',
    margin: 0,
    padding: 0,
    alignItems: 'center',
  },
  linkItem: {
    textDecoration: 'none',
    color: '#9999A1',
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: 'clamp(13px, 1.1vw, 16px)',
    fontWeight: 400,
    lineHeight: '24px',
    cursor: 'pointer',
    transition: 'color 0.3s ease',
  },
  activeLink: { fontWeight: 700, color: '#000000' },

  // More dropdown
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 8,
    background: '#fff',
    border: '1px solid #E6E6E8',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    minWidth: 180,
    zIndex: 200,
    overflow: 'hidden',
  },
  dropdownItem: {
    display: 'block',
    padding: '12px 16px',
    textDecoration: 'none',
    color: '#333343',
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: '15px',
    fontWeight: 400,
    transition: 'background 0.15s',
  },
  dropdownItemActive: {
    fontWeight: 700,
    color: '#1D4094',
    background: 'rgba(29,64,148,0.05)',
  },

  // Login/Signup button
  loginSignupButton: {
    padding: 'clamp(10px, 1.2vw, 16px) clamp(16px, 2.5vw, 32px)',
    border: '1px solid #1D4094',
    backgroundColor: '#1D4094',
    color: '#ffffff',
    fontSize: 'clamp(13px, 1.1vw, 16px)',
    fontWeight: 400,
    fontFamily: '"Funnel Sans", sans-serif',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  // User dropdown (logged in)
  userDropdownBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '16px',
    maxWidth: 220,
    height: 62,
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
  userDropdownName: {
    fontFamily: '"Funnel Sans", sans-serif',
    fontWeight: 400,
    fontSize: 20,
    lineHeight: '30px',
    color: '#1D4094',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 170,
  },
  userDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1px solid #E6E6E8',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    minWidth: 140,
    zIndex: 200,
    overflow: 'hidden',
  },
  userDropdownItem: {
    display: 'block',
    width: '100%',
    padding: '14px 16px',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: 15,
    color: '#333343',
    cursor: 'pointer',
  },

  // Mobile
  hamburger: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '18px',
    cursor: 'pointer',
  },
  closeGlyph: { fontSize: '24px', fontWeight: 'bold', lineHeight: 1 },
  bar: {
    width: '25px',
    height: '3px',
    backgroundColor: '#333',
    borderRadius: '2px',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overscrollBehavior: 'contain',
  },
  modalContent: {
    width: '100%',
    height: 'calc(var(--vh, 1vh) * 100)',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 'max(15px, env(safe-area-inset-top))',
    paddingRight: 'max(20px, env(safe-area-inset-right))',
    paddingBottom: '15px',
    paddingLeft: 'max(20px, env(safe-area-inset-left))',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
  },
  modalLogoWrap: {
    width: '60px',
    height: '60px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  modalLogoImg: { width: '100%', height: '100%', objectFit: 'contain' },
  modalClose: {
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  modalBody: {
    paddingTop: '22px',
    paddingRight: 'max(20px, env(safe-area-inset-right))',
    paddingBottom: 'max(22px, env(safe-area-inset-bottom))',
    paddingLeft: 'max(20px, env(safe-area-inset-left))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: 1,
    overflow: 'hidden',
  },
  mobileList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
  },
  mobileLink: {
    textDecoration: 'none',
    color: '#333',
    fontSize: '18px',
    fontFamily: '"Funnel Sans", sans-serif',
    fontWeight: 600,
  },
  activeMobileLink: { fontWeight: 700, color: '#000' },
  mobileLoginButton: {
    width: '90%',
    padding: '14px',
    backgroundColor: '#1D4094',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  mobileLogoutButton: {
    width: '90%',
    padding: '14px',
    backgroundColor: '#E53935',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  mobileUserText: { marginBottom: '10px', fontSize: '16px', fontWeight: 600 },
};
